import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ViewInAr as ViewIn3dIcon,
} from '@mui/icons-material';
import validator from 'validator';
import {
  Typography,
  InputItemWrapper,
  InfoItem,
  Input,
  Button,
  enqueueSnackbar,
  FileRow,
  BorderLine,
  HideInput,
  FileInput,
  SearchableSelect,
  Select,
  InputNumber,
  yardPlaceSceneModal,
} from '../../design-system';
import {
  DashboardHeader,
  DashboardFormCards,
  DashboardFormCard,
  DashboardFormCustomColumn,
  DashboardFormCardsFooter,
} from '../dashboard';
import {
  useEditCompanyMutation,
  useUpdateClientDocumentsMutation,
  useUpdateYardCompanyLogoMutation,
} from '../../hooks';
import { formatPhoneNumber, getNormalFormProps, showToRoles } from '../../utils';
import {
  canTradeOptions,
  invoiceCreateModeOptions,
  clientCategories,
  gateOutAllowedOptions,
  INPUT_ERRORS,
  MESSAGES,
  messengerOptions,
  REGEX,
  viewCompanyLabel,
} from '../../const';
import { i18n } from '../../i18n';
import { Block, Messenger } from '../../types';
import { useImmer } from 'use-immer';
import { clientsService, yardCompaniesService } from '../../services';
import { checkBlocksScene } from '../../utils';
import { useStoreUser } from '../../context';

let formProps: any = { isEdit: false };

const getSubmitProps = (belongsTo: string) => {
  const {
    validators: { blocks, messengers, descriptions, ...validators },
  }: any = formProps;

  if (!checkBlocksScene(formProps.blocksScene, formProps.blocks)) {
    enqueueSnackbar(MESSAGES.YARD.ERROR.BLOCKS_SCENE_NOT_VALID, 'error');
    return {};
  }

  if (
    [
      !blocks.some((item: any) => !!item.block || !!item.rowLength || !!item.slotLength),
      !messengers.some((item: any) => !!item.label || !!item.identifier),
      ...descriptions.map((val: any) => !val),
      ...Object.values(validators).map((val) => !val),
    ].some((val) => !val)
  ) {
    if (validators.code) enqueueSnackbar(validators.code, 'error');
    return {};
  }

  return {
    submitProps: { ...getNormalFormProps(formProps), belongsTo },
    resolveFn: formProps.resolveFn,
  };
};

export const CompanyViewHeader = ({ data, belongsTo, showOnlySubmit, isEdit, setIsEdit }: any) => {
  const { user } = useStoreUser();
  const pathname = useLocation().pathname.split('/')[2];
  const { mutate } = useEditCompanyMutation({ id: data.id });
  const updateDocumentsMutation = useUpdateClientDocumentsMutation();
  const updateLogoMutation = useUpdateYardCompanyLogoMutation();

  const handleViewIn3dClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();

    yardPlaceSceneModal.blocks = formProps.blocks;
    yardPlaceSceneModal.blocksScene = formProps.blocksScene;
    yardPlaceSceneModal.open();
    yardPlaceSceneModal.submitClick = (blocksScene: string) => {
      formProps.isEdit = true;
      formProps.blocksScene = blocksScene;
      formProps.onSubmitClick();
      setIsEdit(true);
    };
  };

  const handleSubmitClick = async () => {
    formProps.onSubmitClick();

    if (!formProps.isEdit) {
      formProps.isEdit = !formProps.isEdit;
      setIsEdit(true);
      return;
    }
    formProps.isSubmitClicked = true;

    const { submitProps, resolveFn } = getSubmitProps(belongsTo);
    if (!submitProps) return;

    const editMutate = () => {
      mutate(submitProps, {
        onSuccess: () => {
          enqueueSnackbar(MESSAGES.BELONGS_TO.SUCCESS.UPDATED, 'success');
          resolveFn();
          formProps.isEdit = !formProps.isEdit;
          setIsEdit(false);
        },
        onError: (error: any) => enqueueSnackbar(error.response.data.message, 'error'),
      });
    };

    if (belongsTo === 'YARD_COMPANY') {
      if (!submitProps.logo) {
        editMutate();
        return;
      }

      updateLogoMutation.mutate(
        { id: data.id, logo: submitProps.logo },
        {
          onSuccess: (logo) => {
            formProps.logoResult = logo;
            editMutate();
          },
          onError: (error: any) => enqueueSnackbar(error.response.data.message, 'error'),
        },
      );
      return;
    }

    if (belongsTo === 'CLIENT') {
      updateDocumentsMutation.mutate(
        {
          id: data.id,
          documents: submitProps.files,
          descriptions: submitProps.descriptions,
        },
        {
          onSuccess: (documents) => {
            formProps.documents = documents;
          },
          onSettled: () => editMutate(),
        },
      );
      return;
    }

    editMutate();
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {belongsTo === 'YARD' ? data.name : data.legalName}
          </Typography>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} data-color={'white'} to={`/dashboard/${pathname}`}>
        {i18n.t('button.goBack')}
      </Button>
      {belongsTo === 'YARD' && !showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button
          className={'row-button-square'}
          size={'medium'}
          data-color={'primary'}
          isSquare={true}
          onClick={handleViewIn3dClick}
        >
          <ViewIn3dIcon />
        </Button>
      )}
      {!showToRoles(['YARD_COMPANY_VIEWER'], user) && (
        <Button size={'medium'} onClick={handleSubmitClick}>
          {isEdit ? i18n.t('button.save') : i18n.t('button.edit')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const CompanyViewForm = ({ data }: any) => {
  const { user } = useStoreUser();

  const pathname = useLocation().pathname.split('/')[2];
  const [code, setCode] = useState(data.code);
  const [canTrade, setCanTrade] = useState(data.canTrade || false);
  const [invoiceCreateMode, setInvoiceCreateMode] = useState(data.invoiceCreateMode || 'MANUAL');
  const [blocksTier, setBlocksTier] = useState(data.blocksTier ?? 3);
  const [category, setCategory] = useState(data.category || 'NONE');
  const [gateOutAllowed, setGateOutAllowed] = useState(!!data.gateOutAllowed);
  const [comment, setComment] = useState(data.comment || '');
  const [blocks, setBlocks] = useState<Block[]>(
    data.blocks?.length
      ? data.blocks.map((item: Block) => ({
          id: item.id,
          block: item.block,
          rowLength: String(item.rowLength ?? ''),
          slotLength: String(item.slotLength ?? ''),
        }))
      : [{ block: '', rowLength: '', slotLength: '' }],
  );
  const [messengers, setMessengers] = useState<Messenger[]>(
    data.messengers?.length ? data.messengers : [{ label: '', identifier: '' }],
  );

  const [oldFiles, setOldFiles] = useImmer<{ url: string; name: string; description: string }[]>(
    data.documents || [],
  );
  const deleteOldFiles = useRef<any[]>([]);
  const [newFiles, setNewFiles] = useImmer<{ url: string; name: string; description: string }[]>(
    [],
  );
  const [descriptions, setDescriptions] = useState<any>(
    data.documents?.map((item: any) => item.description) || [],
  );
  const inputRef = useRef<any>([]);
  const [logo, setLogo] = useState<{ url: string; name: string } | null>(data.logo || null);
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [isLogoDeleted, setIsLogoDeleted] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>();

  const [updateKey, setUpdate] = useState<any>(Math.random());

  const validators = {
    code:
      pathname === 'clients'
        ? code
          ? !validator.isLength(code, { min: 4, max: 8 })
            ? INPUT_ERRORS.NOT_VALID.CODE_LENGTH
            : !REGEX.CODE_NEW.test(code)
            ? INPUT_ERRORS.NOT_VALID.CODE
            : ''
          : INPUT_ERRORS.REQUIRED.CODE
        : '',
    blocks: blocks.map((item) => ({
      block: item.block
        ? !validator.isLength(item.block, { max: 30 })
          ? INPUT_ERRORS.NOT_VALID.BLOCK_NAME_LENGTH
          : ''
        : '',
    })),
    messengers: messengers.map((item) => ({
      label: item.label
        ? !validator.isLength(item.label, { max: 30 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_NAME_LENGTH
          : ''
        : '',
      identifier: item.identifier
        ? !validator.isLength(item.identifier, { max: 100 })
          ? INPUT_ERRORS.NOT_VALID.MESSENGER_IDENTIFIER_LENGTH
          : ''
        : '',
    })),
    descriptions: descriptions.map((val: string) =>
      val
        ? !validator.isLength(val, { max: 100 })
          ? INPUT_ERRORS.NOT_VALID.FILE_NAME_LENGTH
          : ''
        : INPUT_ERRORS.REQUIRED.FILE_NAME,
    ),
  };

  useEffect(() => {
    if (!formProps) return;

    formProps = { isEdit: false, blocksScene: data.blocksScene };
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    formProps = {
      ...formProps,
      code,
      canTrade,
      invoiceCreateMode,
      blocksTier,
      category,
      gateOutAllowed,
      comment,
      blocks,
      messengers,
      validators,
      descriptions,
      logo: newLogo
        ? { action: 'upload', file: newLogo }
        : isLogoDeleted && logo
        ? { action: 'delete', name: logo.name }
        : undefined,
      files: [
        ...deleteOldFiles.current,
        ...newFiles.map((file: any) => ({
          action: 'upload',
          file: file,
          name: file.name,
        })),
      ],
      onSubmitClick: () => {
        setUpdate(Math.random());
      },
      resolveFn: () => {
        const newBlocks = blocks.filter(
          (item) => !!item.block && !!item.rowLength && !!item.slotLength,
        );
        const newMessengers = messengers.filter((item) => !!item.label && !!item.identifier);

        setBlocks(newBlocks.length ? newBlocks : [{ block: '', rowLength: '', slotLength: '' }]);
        setMessengers(newMessengers.length ? newMessengers : [{ label: '', identifier: '' }]);
        setOldFiles([...oldFiles, ...(formProps.documents || [])]);
        setNewFiles([]);
        deleteOldFiles.current = [];

        if (formProps.logoResult !== undefined) {
          setLogo(formProps.logoResult);
        }
        setNewLogo(null);
        setIsLogoDeleted(false);

        formProps.documents = [];
        formProps.logoResult = undefined;
        setUpdate(Math.random());
      },
    };
  }, [
    code,
    canTrade,
    invoiceCreateMode,
    blocksTier,
    category,
    gateOutAllowed,
    comment,
    JSON.stringify(blocks),
    JSON.stringify(messengers),
    descriptions.join(''),
    JSON.stringify([...oldFiles, ...newFiles]),
    logo?.name,
    newLogo?.name,
    isLogoDeleted,
  ]);

  const handleAddBlockClick = () => {
    setBlocks([...blocks, { block: '', rowLength: '', slotLength: '' }]);
  };

  const handleDeleteBlockClick = (i: number) => {
    setBlocks(blocks.filter((_: any, index: number) => index !== i));

    setUpdate(Math.random());
  };

  const handleAddMessengerClick = () => {
    setMessengers([...messengers, { label: '', identifier: '' }]);
  };

  const handleDeleteClick = (i: number) => {
    setMessengers(messengers.filter((_: any, index: number) => index !== i));

    setUpdate(Math.random());
  };

  const handleDownloadDocumentsClick = (i: number) => {
    const documentDownload = [...oldFiles, ...newFiles];
    if (!documentDownload[i].url) return;

    clientsService.downloadDocument(documentDownload[i].url, documentDownload[i].name);
  };

  const handleDeleteFileClick = (i: number) => {
    const isDeleteNewImage = i - oldFiles.length >= 0;
    setDescriptions(descriptions.filter((_: any, index: number) => index !== i));

    if (isDeleteNewImage) {
      i = i - oldFiles.length;

      setNewFiles((props) => props.filter((_: any, index: number) => index !== i));
    } else {
      deleteOldFiles.current = [
        ...deleteOldFiles.current,
        { action: 'delete', name: oldFiles[i].name },
      ];

      setOldFiles((props) => props.filter((_: any, index: number) => index !== i));
    }
    setUpdate(Math.random());
  };

  const handleChangeFiles = (_: any, newFiles: any) => {
    setNewFiles((props) => [...props, ...newFiles]);
    setDescriptions([...descriptions, ...newFiles.fill('')]);

    inputRef.current.type = 'text';
    inputRef.current.type = 'file';
  };

  const handleChangeLogo = (_: any, files: File[]) => {
    setNewLogo(files[0] || null);
    setIsLogoDeleted(false);

    if (logoInputRef.current) {
      logoInputRef.current.type = 'text';
      logoInputRef.current.type = 'file';
    }
  };

  const handleDeleteLogo = () => {
    if (newLogo) {
      setNewLogo(null);
      return;
    }

    setIsLogoDeleted(true);
  };

  const visibleLogo = newLogo || (!isLogoDeleted ? logo : null);

  const handleDownloadLogo = () => {
    if (!logo?.url || newLogo) return;
    yardCompaniesService.downloadLogo(logo.url, logo.name);
  };

  return (
    <DashboardFormCards id={'company-view'}>
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('common.generalInformation')}
        </Typography>
        {pathname !== 'yards' && (
          <InfoItem label={viewCompanyLabel[pathname]} value={data.legalName} />
        )}
        {pathname === 'yards' && <InfoItem label={viewCompanyLabel[pathname]} value={data.name} />}
        {(!formProps.isEdit || !showToRoles(['SUPER_ADMIN'], user)) && pathname === 'clients' && (
          <InfoItem label={i18n.t('label.code')} value={code} />
        )}
        {pathname !== 'yards' && (
          <InfoItem label={i18n.t('label.companyEmail')} value={data.email} />
        )}
        <InfoItem
          label={
            pathname !== 'yards' ? i18n.t('label.companyPhoneNumber') : i18n.t('label.phoneNumber')
          }
          value={formatPhoneNumber(data.phoneNumber)}
        />
        {pathname === 'yardcompanies' && !formProps.isEdit && !visibleLogo && (
          <InfoItem label={i18n.t('label.logo')} value={null} />
        )}
        {pathname === 'yardcompanies' && !formProps.isEdit && visibleLogo && (
          <FileRow
            file={{ ...visibleLogo, name: i18n.t('label.logo') }}
            handleDeleteFileClick={null}
            handleDownloadClick={logo?.url ? handleDownloadLogo : undefined}
            variant={'row'}
          />
        )}
        {pathname === 'yardcompanies' && formProps.isEdit && (
          <InputItemWrapper>
            <HideInput>
              <FileInput
                id={'yard-company-logo-input'}
                label={i18n.t('label.logo')}
                inputRef={logoInputRef}
                inputProps={{ accept: 'image/*' }}
                handleChangeFiles={handleChangeLogo}
                withoutCompress
              />
            </HideInput>
            <DashboardFormCustomColumn
              columns={visibleLogo ? '1fr' : 'minmax(0, 1fr) max-content'}
              align={'end'}
            >
              {visibleLogo ? (
                <FileRow
                  file={visibleLogo}
                  label={i18n.t('label.logo')}
                  handleDeleteFileClick={handleDeleteLogo}
                  handleDownloadClick={logo?.url && !newLogo ? handleDownloadLogo : undefined}
                  variant={'input'}
                  withoutBorder
                />
              ) : (
                <Input label={i18n.t('label.logo')} data-size={'medium'} readOnly />
              )}
              {!visibleLogo && (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'secondary'}
                  isSquare
                  onClick={() => logoInputRef.current?.click()}
                >
                  <AddIcon />
                </Button>
              )}
            </DashboardFormCustomColumn>
          </InputItemWrapper>
        )}
        {!formProps.isEdit && pathname === 'clients' && (
          <InfoItem
            label={i18n.t('label.category')}
            value={clientCategories.find((item) => item.value === category)?.label}
          />
        )}
        {formProps.isEdit && pathname === 'clients' && (
          <InputItemWrapper>
            <DashboardFormCustomColumn columns={'1fr'}>
              {showToRoles(['SUPER_ADMIN'], user) && (
                <Input
                  defaultValue={code}
                  onChange={(e: any) => setCode(e.target.value)}
                  label={i18n.t('label.code') + '*'}
                  type={'text'}
                  data-size={'medium'}
                  error={formProps.isSubmitClicked ? !!validators.code : ''}
                  helperText={formProps.isSubmitClicked ? validators.code : ''}
                />
              )}
              <Select
                label={i18n.t('label.category')}
                options={clientCategories}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </DashboardFormCustomColumn>
          </InputItemWrapper>
        )}
        {pathname === 'yards' && !formProps.isEdit && (
          <>
            <InfoItem
              label={i18n.t('label.canTrade')}
              value={canTrade ? i18n.t('common.yes') : i18n.t('common.no')}
            />
            <InfoItem
              label={i18n.t('label.invoiceCreateMode')}
              value={
                invoiceCreateModeOptions.find((item) => item.value === invoiceCreateMode)?.label
              }
            />
          </>
        )}
        {pathname === 'yards' && formProps.isEdit && (
          <InputItemWrapper>
            <DashboardFormCustomColumn columns={'1fr'}>
              <Select
                label={i18n.t('label.canTrade')}
                options={canTradeOptions}
                value={canTrade}
                onChange={(e) => setCanTrade(e.target.value)}
              />
              <Select
                label={i18n.t('label.invoiceCreateMode')}
                options={invoiceCreateModeOptions}
                value={invoiceCreateMode}
                onChange={(e) => setInvoiceCreateMode(e.target.value)}
              />
            </DashboardFormCustomColumn>
          </InputItemWrapper>
        )}
        {pathname === 'yards' && !formProps.isEdit && (
          <InfoItem label={i18n.t('label.tier')} value={blocksTier} />
        )}
        {pathname === 'yards' && formProps.isEdit && (
          <InputItemWrapper>
            <InputNumber
              label={i18n.t('label.tier')}
              defaultValue={blocksTier}
              minValue={1}
              maxValue={20}
              onChange={(e: any) => setBlocksTier(e.target.value)}
              data-size={'medium'}
            />
          </InputItemWrapper>
        )}
      </DashboardFormCard>
      {pathname !== 'yards' && (
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.account.contactInformation')}
          </Typography>
          <InfoItem
            label={i18n.t('label.contactName')}
            value={[data.contactPerson?.firstName, data.contactPerson?.lastName]}
          />
          <InfoItem label={i18n.t('label.contactEmail')} value={data.contactPerson?.email} />
          <InfoItem
            label={i18n.t('label.contactPhoneNumber')}
            value={formatPhoneNumber(data.contactPerson?.phoneNumber)}
          />
          {pathname === 'clients' && !formProps.isEdit && (
            <InfoItem label={i18n.t('label.comment')} value={comment} />
          )}
          {pathname === 'clients' && formProps.isEdit && (
            <InputItemWrapper>
              <Input
                multiline
                label={i18n.t('label.comment')}
                data-size={'medium'}
                defaultValue={comment}
                onChange={(e) => setComment(e.target.value.trim())}
              />
            </InputItemWrapper>
          )}
          {!formProps.isEdit &&
            messengers.map((item: Messenger, i: number) => (
              <InfoItem
                key={i}
                label={item.label || i18n.t('label.messengerName')}
                value={item.identifier}
                breakSpaces
              />
            ))}
          {formProps.isEdit && (
            <>
              <InputItemWrapper>
                <DashboardFormCustomColumn
                  columns={'calc(100%/2 - 2.8rem*(1/2)) 1fr max-content'}
                  align={'start'}
                >
                  {messengers.map((_: any, i: number) => (
                    <React.Fragment key={updateKey + i}>
                      <SearchableSelect
                        id={i18n.t('label.messengerName') + i}
                        selectedOptionLabel={messengers[i].label}
                        label={i18n.t('label.messengerName')}
                        queryFn={(inputValue) => {
                          const isNewOption = !messengerOptions.filter((item) =>
                            item.label.toLowerCase().includes(inputValue.toLowerCase()),
                          ).length;
                          if (isNewOption) {
                            return () => [{ label: inputValue, value: inputValue }];
                          }

                          return () => messengerOptions;
                        }}
                        queryKey={'messengers'}
                        selectedAreasEqaul={{ key: 'value', keys: [messengers[i].label] }}
                        onChange={(e: any) => {
                          const newState = messengers;
                          newState[i].label = e.target.value;
                          setMessengers([...newState]);
                        }}
                        error={formProps.isSubmitClicked && !!validators.messengers[i].label}
                        helperText={formProps.isSubmitClicked && validators.messengers[i].label}
                      />
                      <Input
                        id={i18n.t('label.messengerIdentifier') + i}
                        label={i18n.t('label.messengerIdentifier')}
                        defaultValue={messengers[i].identifier}
                        onChange={(e: any) => {
                          const newState = messengers;
                          newState[i].identifier = e.target.value;
                          setMessengers([...newState]);
                        }}
                        type={'text'}
                        data-size={'medium'}
                        error={formProps.isSubmitClicked && !!validators.messengers[i].identifier}
                        helperText={
                          formProps.isSubmitClicked && validators.messengers[i].identifier
                        }
                      />
                      {i === messengers.length - 1 ? (
                        <Button
                          className={'row-button-square'}
                          size={'medium'}
                          data-color={'secondary'}
                          isSquare={true}
                          onClick={handleAddMessengerClick}
                        >
                          <AddIcon />
                        </Button>
                      ) : (
                        <Button
                          className={'row-button-square'}
                          size={'medium'}
                          data-color={'destructiveSecondary'}
                          isSquare={true}
                          onClick={() => handleDeleteClick(i)}
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </DashboardFormCustomColumn>
              </InputItemWrapper>
            </>
          )}
        </DashboardFormCard>
      )}
      <DashboardFormCard paperSize={'full-size'}>
        <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
          {i18n.t('pages.account.addressInformation')}
        </Typography>
        <InfoItem label={i18n.t('label.country')} value={data.address?.country} />
        <InfoItem label={i18n.t('label.city')} value={data.address?.city} />
        <InfoItem label={i18n.t('label.streetAddress')} value={data.address?.streetAddress} />
        <InfoItem label={i18n.t('label.zipCode')} value={data.address?.zipCode} />
      </DashboardFormCard>
      {pathname === 'yards' && (
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.account.blocks')}
          </Typography>
          {!formProps.isEdit &&
            blocks.map((item: Block, i: number) => (
              <InfoItem
                key={i}
                label={item.block || i18n.t('label.block')}
                value={`${item.rowLength ? `${i18n.t('label.rows')}:${item.rowLength}` : ''}${
                  item.slotLength ? `, ${i18n.t('label.slots')}:${item.slotLength}` : ''
                }`}
                breakSpaces
              />
            ))}
          {formProps.isEdit && (
            <InputItemWrapper>
              <DashboardFormCustomColumn columns={'1fr 1fr 1fr max-content'} align={'start'}>
                {blocks.map((item: Block, i: number) => (
                  <React.Fragment key={updateKey + i}>
                    <Input
                      id={i18n.t('label.block') + i}
                      label={i18n.t('label.block')}
                      defaultValue={item.block}
                      onChange={(e: any) => {
                        const newState = blocks;
                        newState[i].block = e.target.value;
                        setBlocks([...newState]);
                      }}
                      type={'text'}
                      data-size={'medium'}
                      error={formProps.isSubmitClicked && !!validators.blocks[i].block}
                      helperText={formProps.isSubmitClicked && validators.blocks[i].block}
                    />
                    <InputNumber
                      id={i18n.t('label.rowLength') + i}
                      label={i18n.t('label.rowLength')}
                      defaultValue={item.rowLength}
                      minValue={1}
                      maxValue={40}
                      onChange={(e: any) => {
                        const newState = blocks;
                        newState[i].rowLength = e.target.value;
                        setBlocks([...newState]);
                      }}
                      data-size={'medium'}
                    />
                    <InputNumber
                      id={i18n.t('label.slotLength') + i}
                      label={i18n.t('label.slotLength')}
                      defaultValue={item.slotLength}
                      minValue={1}
                      maxValue={40}
                      onChange={(e: any) => {
                        const newState = blocks;
                        newState[i].slotLength = e.target.value;
                        setBlocks([...newState]);
                      }}
                      data-size={'medium'}
                    />
                    {i === blocks.length - 1 ? (
                      <Button
                        className={'row-button-square'}
                        size={'medium'}
                        data-color={'secondary'}
                        isSquare={true}
                        onClick={handleAddBlockClick}
                      >
                        <AddIcon />
                      </Button>
                    ) : (
                      <Button
                        className={'row-button-square'}
                        size={'medium'}
                        data-color={'destructiveSecondary'}
                        isSquare={true}
                        onClick={() => handleDeleteBlockClick(i)}
                      >
                        <DeleteIcon />
                      </Button>
                    )}
                  </React.Fragment>
                ))}
              </DashboardFormCustomColumn>
            </InputItemWrapper>
          )}
        </DashboardFormCard>
      )}
      {pathname === 'yards' && (
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.account.messengers')}
          </Typography>
          {!formProps.isEdit &&
            messengers.map((item: Messenger, i: number) => (
              <InfoItem
                key={i}
                label={item.label || i18n.t('label.messengerName')}
                value={item.identifier}
                breakSpaces
              />
            ))}
          {formProps.isEdit && (
            <>
              <InputItemWrapper>
                <DashboardFormCustomColumn
                  columns={'calc(100%/2 - 2.8rem*(1/2)) 1fr max-content'}
                  align={'start'}
                >
                  {messengers.map((_: any, i: number) => (
                    <React.Fragment key={updateKey + i}>
                      <SearchableSelect
                        id={i18n.t('label.messengerName') + i}
                        selectedOptionLabel={messengers[i].label}
                        label={i18n.t('label.messengerName')}
                        queryFn={(inputValue) => {
                          const isNewOption = !messengerOptions.filter((item) =>
                            item.label.toLowerCase().includes(inputValue.toLowerCase()),
                          ).length;
                          if (isNewOption) {
                            return () => [{ label: inputValue, value: inputValue }];
                          }

                          return () => messengerOptions;
                        }}
                        queryKey={'messengers'}
                        selectedAreasEqaul={{ key: 'value', keys: [messengers[i].label] }}
                        onChange={(e: any) => {
                          const newState = messengers;
                          newState[i].label = e.target.value;
                          setMessengers([...newState]);
                        }}
                        error={formProps.isSubmitClicked && !!validators.messengers[i].label}
                        helperText={formProps.isSubmitClicked && validators.messengers[i].label}
                      />
                      <Input
                        id={i18n.t('label.messengerIdentifier') + i}
                        label={i18n.t('label.messengerIdentifier')}
                        defaultValue={messengers[i].identifier}
                        onChange={(e: any) => {
                          const newState = messengers;
                          newState[i].identifier = e.target.value;
                          setMessengers([...newState]);
                        }}
                        type={'text'}
                        data-size={'medium'}
                        error={formProps.isSubmitClicked && !!validators.messengers[i].identifier}
                        helperText={
                          formProps.isSubmitClicked && validators.messengers[i].identifier
                        }
                      />
                      {i === messengers.length - 1 ? (
                        <Button
                          className={'row-button-square'}
                          size={'medium'}
                          data-color={'secondary'}
                          isSquare={true}
                          onClick={handleAddMessengerClick}
                        >
                          <AddIcon />
                        </Button>
                      ) : (
                        <Button
                          className={'row-button-square'}
                          size={'medium'}
                          data-color={'destructiveSecondary'}
                          isSquare={true}
                          onClick={() => handleDeleteClick(i)}
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </DashboardFormCustomColumn>
              </InputItemWrapper>
            </>
          )}
        </DashboardFormCard>
      )}
      {pathname === 'clients' && (
        <DashboardFormCard paperSize={'full-size'}>
          <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
            {i18n.t('pages.account.settings')}
          </Typography>
          {!formProps.isEdit && (
            <InfoItem
              label={i18n.t('label.gateOutAuthorization')}
              value={
                gateOutAllowed
                  ? i18n.t('dropdown.gateOutAutomatic')
                  : i18n.t('dropdown.gateOutDispatcherApprovalRequired')
              }
            />
          )}
          {formProps.isEdit && (
            <InputItemWrapper>
              <Select
                label={i18n.t('label.gateOutAuthorization')}
                options={gateOutAllowedOptions}
                value={gateOutAllowed}
                onChange={(e) => setGateOutAllowed(e.target.value)}
              />
            </InputItemWrapper>
          )}
        </DashboardFormCard>
      )}
      {pathname === 'clients' && (
        <DashboardFormCardsFooter>
          <DashboardFormCard paperSize={'full-size'}>
            <Typography variant={'textL'} weight={'semibold'} color={'--gray-90'}>
              {i18n.t('pages.account.documents')}
            </Typography>
            {!formProps.isEdit &&
              [...oldFiles, ...newFiles].map((file, i) => (
                <FileRow
                  key={i}
                  file={{ ...file, name: descriptions[i] || file.name }}
                  handleDeleteFileClick={null}
                  handleDownloadClick={file.url ? () => handleDownloadDocumentsClick(i) : undefined}
                  variant={'row'}
                />
              ))}
            {formProps.isEdit && (
              <>
                <DashboardFormCustomColumn
                  key={updateKey + 'files'}
                  columns={'1fr 1fr'}
                  align={'start'}
                >
                  <BorderLine space={'none'} gridColumn={'1 / 3'} />
                  {[...oldFiles, ...newFiles].map((file, i) => (
                    <React.Fragment key={updateKey + 'files' + i}>
                      <Input
                        id={i18n.t('label.fileName') + i}
                        label={i18n.t('label.fileName')}
                        defaultValue={descriptions[i]}
                        onChange={(e: any) => {
                          const newState = descriptions;
                          newState[i] = e.target.value;
                          setDescriptions([...newState]);
                        }}
                        type={'text'}
                        data-size={'medium'}
                        error={formProps.isSubmitClicked && !!validators.descriptions[i]}
                        helperText={formProps.isSubmitClicked && validators.descriptions[i]}
                      />
                      <FileRow
                        file={file}
                        handleDeleteFileClick={() => handleDeleteFileClick(i)}
                        variant={'input'}
                        withoutBorder
                      />
                    </React.Fragment>
                  ))}
                  <HideInput>
                    <FileInput
                      id={'file-input'}
                      label={i18n.t('dropdown.addImages')}
                      inputRef={inputRef}
                      inputProps={{
                        accept: '*',
                        multiple: true,
                      }}
                      handleChangeFiles={handleChangeFiles}
                      withoutCompress
                    />
                  </HideInput>
                  {formProps.isEdit && (
                    <Button
                      size={'medium'}
                      justify={'left'}
                      onClick={() => inputRef.current.click()}
                    >
                      {i18n.t('button.addDocuments')}
                    </Button>
                  )}
                </DashboardFormCustomColumn>
              </>
            )}
          </DashboardFormCard>
        </DashboardFormCardsFooter>
      )}
    </DashboardFormCards>
  );
};
